/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import React, { useCallback, useEffect, useMemo } from "react";
import { SSOAuthButton as SSOButton } from "./components/SSOAuthButton";
import { useGoogleLogin } from "@react-oauth/google";
import { useAppleAuth } from "@/hooks/auth/useAppleAuth";
import { useSSOState, clearSSOToasts } from "@/components/SSOAuthButton/hooks/useSSOState";
import { useSSOAuth } from "@/components/SSOAuthButton/hooks/useSSOAuth";
import { useToast } from "@/hooks/ui/useToast";
import { useLogin } from "@/hooks/auth/useLogin";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";

import { SSO_PROVIDER, SsoProviderId, type SSOAuthContainerProps } from "./types";

export const SSOAuthContainer = ({
  mode,
  providers = [SSO_PROVIDER.GOOGLE, SSO_PROVIDER.APPLE],
  rounded,
  isFormLoading,
  labels,
  onSsoCompleted,
  onSsoLoadingChange,
  errors,
}: SSOAuthContainerProps) => {
  const { toast } = useToast();
  const { onSaveCookie } = useLogin();

  const {
    setSsoLoading,
    isSsoLoading,
    activeProvider,
    accessToken,
    setAccessToken,
    resetAllAuthState,
  } = useSSOState();

  const providerKey = activeProvider ?? SSO_PROVIDER.GOOGLE; // Use activeProvider to select the right handlers

  // SSO Auth Logic accordiing to provider
  const ssoLogic = useSSOAuth({
    provider: providerKey,
    onSaveCookie,
    onSsoCompleted,
    accessToken,
    errors,
    setSsoLoading,
  });

  // Reset centralizado al cambiar de modo (SignIn/SignUp)
  useEffect(() => {
    resetAllAuthState([ssoLogic]);
  }, [mode, resetAllAuthState]);

  // Auth handlers - now simplified and delegated to hooks
  // Loading state sync
  // Sincronización del estado de carga global
  const isApiLoading = ssoLogic.isLoading;
  useEffect(() => {
    onSsoLoadingChange?.(isSsoLoading || isApiLoading);
  }, [isSsoLoading, isApiLoading, onSsoLoadingChange]);

  // Unify handler
  const executeAuthActionRef = React.useRef<(token: string) => void>();

  const executeAuthAction = useCallback(
    (token: string) => {
      if (mode === "signin") {
        ssoLogic.handleLogin(token);
      } else {
        ssoLogic.handleRegister(token);
      }
    },
    [mode, ssoLogic]
  );

  useEffect(() => {
    executeAuthActionRef.current = executeAuthAction;
  }, [executeAuthAction]);

  // --- GOOGLE LOGIC ---
  const runGoogleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: (response) => {
      setAccessToken(response.access_token);
      executeAuthActionRef.current?.(response.access_token);
    },
    onError: (error) => {
      const message = ApiErrorMessageBuilder.buildToastMessage(error, {
        fallbackMessage: API_ERROR_FALLBACKS.AUTH.GOOGLE,
      });
      toast({ message, type: "error" });
      setSsoLoading(false, null);
    },
    onNonOAuthError: (event) => {
      if (["popup_closed", "unknown"].includes(event.type)) {
        setSsoLoading(false, null);
      }
    },
  });

  const runAppleLogin = useAppleAuth({
    onSuccess: (response) => {
      const idToken = response?.authorization?.id_token;
      if (idToken) {
        setAccessToken(idToken);
        executeAuthActionRef.current?.(idToken);
      } else {
        setSsoLoading(false, null);
      }
    },
    onError: () => {
      setSsoLoading(false, null);
    },
  });

  const handleGoogleClick = useCallback(() => {
    // Clear any existing error toasts before starting new login attempt
    clearSSOToasts();
    setSsoLoading(true, SSO_PROVIDER.GOOGLE);
    runGoogleLogin();
  }, [runGoogleLogin, setSsoLoading]);

  // --- APPLE LOGIC ---
  const handleAppleClick = useCallback(() => {
    // Clear any existing error toasts before starting new login attempt
    clearSSOToasts();
    setSsoLoading(true, SSO_PROVIDER.APPLE);
    runAppleLogin.signIn();
  }, [runAppleLogin, setSsoLoading]);

  const handlers = useMemo(
    () => ({
      [SSO_PROVIDER.GOOGLE]: handleGoogleClick,
      [SSO_PROVIDER.APPLE]: handleAppleClick,
    }),
    [handleGoogleClick, handleAppleClick]
  );

  // Render Helpers
  const isSsoLocked = isSsoLoading || isApiLoading;

  const getProviderLabels = useCallback(
    (provider: SsoProviderId) => {
      const providerLabelData = labels[provider];

      return {
        label: providerLabelData?.label || "Unknown",
        loadingLabel: providerLabelData?.loading || "Loading",
      };
    },
    [labels]
  );

  const renderedButtons = useMemo(() => {
    return providers.map((provider) => {
      const isActive = activeProvider === provider;
      const { label, loadingLabel } = getProviderLabels(provider);

      return (
        <SSOButton
          key={provider}
          provider={provider}
          label={label}
          loadingLabel={loadingLabel}
          isShowingLoadingLabel={isSsoLoading && isActive}
          isSsoLocked={isSsoLocked}
          isFormLoading={isFormLoading}
          rounded={rounded}
          onClick={handlers[provider]}
          ariaBusy={isSsoLoading && isActive}
        />
      );
    });
  }, [
    providers,
    activeProvider,
    isSsoLoading,
    isSsoLocked,
    isFormLoading,
    rounded,
    handlers,
    getProviderLabels,
  ]);

  return <>{renderedButtons}</>;
};
